import { useState } from 'react';
import { useLocale } from '../../lib/locale';
import {
  X,
  MapPin,
  Star,
  IndianRupee,
  Clock,
  Utensils,
  Home,
  TreePine,
  Heart,
  Users,
  Cake,
  Briefcase,
  Tag,
  CalendarCheck,
  Leaf,
  Wine,
  TrendingUp,
  Sparkles,
  Wifi,
  Music,
  Car,
  Baby,
  CreditCard,
  UserCheck,
  Shield,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

export interface DineOutFilters {
  // Location & Distance
  distance: string[];
  areas: string[];
  
  // Cuisine Type
  cuisines: string[];
  
  // Price Range
  priceRanges: string[];
  
  // Rating
  minRating: number | null;
  
  // Open Now
  openNow: boolean;
  openAtTime: string;
  
  // Dining Experience
  diningExperience: string[];
  
  // Seating Type
  seatingType: string[];
  
  // Occasion
  occasions: string[];
  
  // Offers
  offers: string[];
  
  // Table Booking
  tableBooking: string[];
  
  // Dietary Preferences
  dietaryPreferences: string[];
  
  // Alcohol
  alcohol: string[];
  
  // Popularity
  popularity: string[];
  
  // Sort By
  sortBy: string;
  
  // Amenities
  amenities: string[];
  
  // Payment Options
  paymentOptions: string[];
  
  // Crowd Level
  crowdLevel: string[];
  
  // Safety & Hygiene
  safetyHygiene: string[];
}

interface DineOutFiltersModalProps {
  onClose: () => void;
  onApplyFilters: (filters: DineOutFilters) => void;
  initialFilters: DineOutFilters;
}

interface FilterSection {
  id: string;
  title: string;
  icon: any;
  expanded: boolean;
}

export function DineOutFiltersModal({ onClose, onApplyFilters, initialFilters }: DineOutFiltersModalProps) {
  const { formatMoney } = useLocale();
  const [filters, setFilters] = useState<DineOutFilters>(initialFilters);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['distance', 'cuisines', 'priceRanges', 'minRating']));

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const toggleArrayFilter = (key: keyof DineOutFilters, value: string) => {
    const currentArray = filters[key] as string[];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(v => v !== value)
      : [...currentArray, value];
    setFilters({ ...filters, [key]: newArray });
  };

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleClearAll = () => {
    setFilters({
      distance: [],
      areas: [],
      cuisines: [],
      priceRanges: [],
      minRating: null,
      openNow: false,
      openAtTime: '',
      diningExperience: [],
      seatingType: [],
      occasions: [],
      offers: [],
      tableBooking: [],
      dietaryPreferences: [],
      alcohol: [],
      popularity: [],
      sortBy: 'relevance',
      amenities: [],
      paymentOptions: [],
      crowdLevel: [],
      safetyHygiene: []
    });
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.distance.length > 0) count++;
    if (filters.areas.length > 0) count++;
    if (filters.cuisines.length > 0) count++;
    if (filters.priceRanges.length > 0) count++;
    if (filters.minRating !== null) count++;
    if (filters.openNow) count++;
    if (filters.diningExperience.length > 0) count++;
    if (filters.seatingType.length > 0) count++;
    if (filters.occasions.length > 0) count++;
    if (filters.offers.length > 0) count++;
    if (filters.tableBooking.length > 0) count++;
    if (filters.dietaryPreferences.length > 0) count++;
    if (filters.alcohol.length > 0) count++;
    if (filters.popularity.length > 0) count++;
    if (filters.sortBy !== 'relevance') count++;
    if (filters.amenities.length > 0) count++;
    if (filters.paymentOptions.length > 0) count++;
    if (filters.crowdLevel.length > 0) count++;
    if (filters.safetyHygiene.length > 0) count++;
    return count;
  };

  const FilterChip = ({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) => (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
        selected
          ? 'border-[#60A5FA] bg-blue-50 text-[#60A5FA]'
          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
      }`}
    >
      {label}
    </button>
  );

  const priceRangeOptions = [
    { value: '0-500', label: `${formatMoney(0)} - ${formatMoney(500)}` },
    { value: '500-1000', label: `${formatMoney(500)} - ${formatMoney(1000)}` },
    { value: '1000-2000', label: `${formatMoney(1000)} - ${formatMoney(2000)}` },
    { value: '2000+', label: `${formatMoney(2000)}+` },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
      <div className="bg-white w-full md:max-w-4xl md:rounded-2xl rounded-t-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-900">Filters</h2>
            {getActiveFiltersCount() > 0 && (
              <span className="bg-[#60A5FA] text-white text-sm px-2.5 py-0.5 rounded-full font-medium">
                {getActiveFiltersCount()}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleClearAll}
              className="text-sm text-gray-600 hover:text-gray-900 font-medium"
            >
              Clear All
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Location & Distance */}
          <FilterSection
            title="Location & Distance"
            icon={MapPin}
            expanded={expandedSections.has('distance')}
            onToggle={() => toggleSection('distance')}
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Distance</label>
                <div className="flex flex-wrap gap-2">
                  {['Near Me', 'Within 1 km', 'Within 3 km', 'Within 5 km'].map(option => (
                    <FilterChip
                      key={option}
                      label={option}
                      selected={filters.distance.includes(option)}
                      onClick={() => toggleArrayFilter('distance', option)}
                    />
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Popular Areas</label>
                <div className="flex flex-wrap gap-2">
                  {['MG Road', 'Indiranagar', 'Koramangala', 'Whitefield', 'HSR Layout'].map(option => (
                    <FilterChip
                      key={option}
                      label={option}
                      selected={filters.areas.includes(option)}
                      onClick={() => toggleArrayFilter('areas', option)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </FilterSection>

          {/* Cuisine Type */}
          <FilterSection
            title="Cuisine Type"
            icon={Utensils}
            expanded={expandedSections.has('cuisines')}
            onToggle={() => toggleSection('cuisines')}
          >
            <div className="flex flex-wrap gap-2">
              {['North Indian', 'South Indian', 'Chinese', 'Italian', 'Continental', 'Street Food', 'Cafe', 'Fast Food', 'Multi-Cuisine'].map(option => (
                <FilterChip
                  key={option}
                  label={option}
                  selected={filters.cuisines.includes(option)}
                  onClick={() => toggleArrayFilter('cuisines', option)}
                />
              ))}
            </div>
          </FilterSection>

          {/* Price Range */}
          <FilterSection
            title="Price Range (For Two)"
            icon={IndianRupee}
            expanded={expandedSections.has('priceRanges')}
            onToggle={() => toggleSection('priceRanges')}
          >
            <div className="flex flex-wrap gap-2">
              {priceRangeOptions.map(option => (
                <FilterChip
                  key={option.value}
                  label={option.label}
                  selected={filters.priceRanges.includes(option.value)}
                  onClick={() => toggleArrayFilter('priceRanges', option.value)}
                />
              ))}
            </div>
          </FilterSection>

          {/* Rating */}
          <FilterSection
            title="Rating"
            icon={Star}
            expanded={expandedSections.has('minRating')}
            onToggle={() => toggleSection('minRating')}
          >
            <div className="flex flex-wrap gap-2">
              {[4.5, 4.0, 3.5, 0].map(rating => (
                <button
                  key={rating}
                  onClick={() => setFilters({ ...filters, minRating: rating === 0 ? null : rating })}
                  className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all flex items-center gap-1.5 ${
                    filters.minRating === rating || (rating === 0 && filters.minRating === null)
                      ? 'border-[#60A5FA] bg-blue-50 text-[#60A5FA]'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {rating > 0 ? (
                    <>
                      <Star className="w-4 h-4 fill-current" />
                      {rating}+
                    </>
                  ) : (
                    'All Ratings'
                  )}
                </button>
              ))}
            </div>
          </FilterSection>

          {/* Open Now */}
          <FilterSection
            title="Availability"
            icon={Clock}
            expanded={expandedSections.has('openNow')}
            onToggle={() => toggleSection('openNow')}
          >
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.openNow}
                  onChange={(e) => setFilters({ ...filters, openNow: e.target.checked })}
                  className="w-5 h-5 text-[#60A5FA] border-gray-300 rounded focus:ring-[#60A5FA]"
                />
                <span className="text-sm font-medium text-gray-700">Open Now</span>
              </label>
            </div>
          </FilterSection>

          {/* Dining Experience */}
          <FilterSection
            title="Dining Experience"
            icon={Sparkles}
            expanded={expandedSections.has('diningExperience')}
            onToggle={() => toggleSection('diningExperience')}
          >
            <div className="flex flex-wrap gap-2">
              {['Casual Dining', 'Fine Dining', 'Cafe', 'Rooftop', 'Family Restaurant', 'Romantic Dining', 'Buffet'].map(option => (
                <FilterChip
                  key={option}
                  label={option}
                  selected={filters.diningExperience.includes(option)}
                  onClick={() => toggleArrayFilter('diningExperience', option)}
                />
              ))}
            </div>
          </FilterSection>

          {/* Seating Type */}
          <FilterSection
            title="Seating Type"
            icon={Home}
            expanded={expandedSections.has('seatingType')}
            onToggle={() => toggleSection('seatingType')}
          >
            <div className="flex flex-wrap gap-2">
              {['Indoor Seating', 'Outdoor Seating', 'Rooftop Seating', 'Private Dining Room'].map(option => (
                <FilterChip
                  key={option}
                  label={option}
                  selected={filters.seatingType.includes(option)}
                  onClick={() => toggleArrayFilter('seatingType', option)}
                />
              ))}
            </div>
          </FilterSection>

          {/* Occasion */}
          <FilterSection
            title="Occasion"
            icon={Heart}
            expanded={expandedSections.has('occasions')}
            onToggle={() => toggleSection('occasions')}
          >
            <div className="flex flex-wrap gap-2">
              {[
                { label: 'Date Night ❤️', value: 'Date Night' },
                { label: 'Family Dinner 👨‍👩‍👧', value: 'Family Dinner' },
                { label: 'Birthday 🎂', value: 'Birthday' },
                { label: 'Anniversary 💍', value: 'Anniversary' },
                { label: 'Corporate Lunch', value: 'Corporate Lunch' },
                { label: 'Friends Hangout', value: 'Friends Hangout' }
              ].map(option => (
                <FilterChip
                  key={option.value}
                  label={option.label}
                  selected={filters.occasions.includes(option.value)}
                  onClick={() => toggleArrayFilter('occasions', option.value)}
                />
              ))}
            </div>
          </FilterSection>

          {/* Offers */}
          <FilterSection
            title="Offers & Deals"
            icon={Tag}
            expanded={expandedSections.has('offers')}
            onToggle={() => toggleSection('offers')}
          >
            <div className="flex flex-wrap gap-2">
              {['Flat Discounts', 'Happy Hours', 'Bank Offers', 'Special Events', 'Festival Offers'].map(option => (
                <FilterChip
                  key={option}
                  label={option}
                  selected={filters.offers.includes(option)}
                  onClick={() => toggleArrayFilter('offers', option)}
                />
              ))}
            </div>
          </FilterSection>

          {/* Table Booking */}
          <FilterSection
            title="Table Booking Availability"
            icon={CalendarCheck}
            expanded={expandedSections.has('tableBooking')}
            onToggle={() => toggleSection('tableBooking')}
          >
            <div className="flex flex-wrap gap-2">
              {['Table Available Now', 'Instant Booking', 'Book for Later'].map(option => (
                <FilterChip
                  key={option}
                  label={option}
                  selected={filters.tableBooking.includes(option)}
                  onClick={() => toggleArrayFilter('tableBooking', option)}
                />
              ))}
            </div>
          </FilterSection>

          {/* Dietary Preferences */}
          <FilterSection
            title="Dietary Preferences"
            icon={Leaf}
            expanded={expandedSections.has('dietaryPreferences')}
            onToggle={() => toggleSection('dietaryPreferences')}
          >
            <div className="flex flex-wrap gap-2">
              {['Pure Veg', 'Jain Food', 'Vegan', 'Halal'].map(option => (
                <FilterChip
                  key={option}
                  label={option}
                  selected={filters.dietaryPreferences.includes(option)}
                  onClick={() => toggleArrayFilter('dietaryPreferences', option)}
                />
              ))}
            </div>
          </FilterSection>

          {/* Alcohol */}
          <FilterSection
            title="Alcohol Availability"
            icon={Wine}
            expanded={expandedSections.has('alcohol')}
            onToggle={() => toggleSection('alcohol')}
          >
            <div className="flex flex-wrap gap-2">
              {['Serves Alcohol 🍺', 'Bar Available', 'BYOB'].map(option => (
                <FilterChip
                  key={option}
                  label={option}
                  selected={filters.alcohol.includes(option)}
                  onClick={() => toggleArrayFilter('alcohol', option)}
                />
              ))}
            </div>
          </FilterSection>

          {/* Popularity */}
          <FilterSection
            title="Popularity"
            icon={TrendingUp}
            expanded={expandedSections.has('popularity')}
            onToggle={() => toggleSection('popularity')}
          >
            <div className="flex flex-wrap gap-2">
              {['Trending Now 🔥', 'Newly Opened', 'Most Booked'].map(option => (
                <FilterChip
                  key={option}
                  label={option}
                  selected={filters.popularity.includes(option)}
                  onClick={() => toggleArrayFilter('popularity', option)}
                />
              ))}
            </div>
          </FilterSection>

          {/* Sort By */}
          <FilterSection
            title="Sort By"
            icon={Star}
            expanded={expandedSections.has('sortBy')}
            onToggle={() => toggleSection('sortBy')}
          >
            <div className="flex flex-wrap gap-2">
              {[
                { label: 'Relevance', value: 'relevance' },
                { label: 'Rating: High to Low', value: 'rating-desc' },
                { label: 'Price: Low to High', value: 'price-asc' },
                { label: 'Distance', value: 'distance' },
                { label: 'Popularity', value: 'popularity' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => setFilters({ ...filters, sortBy: option.value })}
                  className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                    filters.sortBy === option.value
                      ? 'border-[#60A5FA] bg-blue-50 text-[#60A5FA]'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </FilterSection>

          {/* Amenities */}
          <FilterSection
            title="Amenities"
            icon={Wifi}
            expanded={expandedSections.has('amenities')}
            onToggle={() => toggleSection('amenities')}
          >
            <div className="flex flex-wrap gap-2">
              {['Free Wi-Fi', 'Live Music 🎶', 'Parking Available 🚗', 'Valet Parking', 'Wheelchair Accessible ♿', 'Kids Friendly'].map(option => (
                <FilterChip
                  key={option}
                  label={option}
                  selected={filters.amenities.includes(option)}
                  onClick={() => toggleArrayFilter('amenities', option)}
                />
              ))}
            </div>
          </FilterSection>

          {/* Payment Options */}
          <FilterSection
            title="Payment Options"
            icon={CreditCard}
            expanded={expandedSections.has('paymentOptions')}
            onToggle={() => toggleSection('paymentOptions')}
          >
            <div className="flex flex-wrap gap-2">
              {['Accepts Cards', 'UPI Accepted', 'Cash Accepted'].map(option => (
                <FilterChip
                  key={option}
                  label={option}
                  selected={filters.paymentOptions.includes(option)}
                  onClick={() => toggleArrayFilter('paymentOptions', option)}
                />
              ))}
            </div>
          </FilterSection>

          {/* Crowd Level */}
          <FilterSection
            title="Crowd Level"
            icon={Users}
            expanded={expandedSections.has('crowdLevel')}
            onToggle={() => toggleSection('crowdLevel')}
          >
            <div className="flex flex-wrap gap-2">
              {['Not Crowded', 'Moderately Crowded', 'Busy'].map(option => (
                <FilterChip
                  key={option}
                  label={option}
                  selected={filters.crowdLevel.includes(option)}
                  onClick={() => toggleArrayFilter('crowdLevel', option)}
                />
              ))}
            </div>
          </FilterSection>

          {/* Safety & Hygiene */}
          <FilterSection
            title="Safety & Hygiene"
            icon={Shield}
            expanded={expandedSections.has('safetyHygiene')}
            onToggle={() => toggleSection('safetyHygiene')}
          >
            <div className="flex flex-wrap gap-2">
              {['Verified Restaurant', 'Hygiene Rated', 'CCTV Available'].map(option => (
                <FilterChip
                  key={option}
                  label={option}
                  selected={filters.safetyHygiene.includes(option)}
                  onClick={() => toggleArrayFilter('safetyHygiene', option)}
                />
              ))}
            </div>
          </FilterSection>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="flex-1 py-3 rounded-xl font-semibold text-white bg-[#60A5FA] hover:bg-[#3B82F6] transition-colors shadow-lg shadow-blue-200"
          >
            Apply Filters {getActiveFiltersCount() > 0 && `(${getActiveFiltersCount()})`}
          </button>
        </div>
      </div>
    </div>
  );
}

function FilterSection({
  title,
  icon: Icon,
  expanded,
  onToggle,
  children
}: {
  title: string;
  icon: any;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-[#60A5FA]" />
          <span className="font-semibold text-gray-900">{title}</span>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-gray-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-600" />
        )}
      </button>
      {expanded && (
        <div className="p-4 bg-white">
          {children}
        </div>
      )}
    </div>
  );
}
